import { CommonModule } from '@angular/common';

import { HttpErrorResponse } from '@angular/common/http';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  signal,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';

import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { isFreeEmail } from 'free-email-domains-list';
import { Subject, catchError, map, take, takeUntil, throwError } from 'rxjs';

import { OktaUserService } from 'src/app/manage-users/services/okta-user/okta-user.service';
import { User } from 'src/app/models/user.model';
import { Permission } from 'src/app/shared/constants/permissions';
import { SharedModule } from 'src/app/shared/shared.module';
import { ToastNotificationComponent } from '../../shared/components/toast-notification/toast-notification.component';
import * as fromStore from '../../store';
import {
  FEDERATED_LOGOUT_SECONDS,
  OKTA_MIGRATION_STORAGE_KEY_EMAIL,
  OKTA_MIGRATION_STORAGE_KEY_EXPIRES_AT,
  OKTA_MIGRATION_STORAGE_KEY_STEP,
  OktaMigrationTimerService,
} from './okta-migration-timer.service';

export interface StartOktaTransitionRequest {
  email: string;
}

export interface StartOktaTransitionResponse {
  success: boolean;
  email: string;
}

@Component({
  selector: 'app-okta-migration',

  standalone: true,

  templateUrl: './okta-migration.component.html',

  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    RouterLink,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
})
export class OktaMigrationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly oktaUserService = inject(OktaUserService);

  private readonly snackBar = inject(MatSnackBar);

  private store = inject(Store<fromStore.State>);

  private readonly renderer = inject(Renderer2);

  private readonly router = inject(Router);

  private readonly meta = inject(Meta);

  readonly migrationTimer = inject(OktaMigrationTimerService);

  private destroy$ = new Subject<void>();

  private popStateListener: (() => void) | null = null;

  loggedInUser = signal<User | null>(null);

  isLoading = signal(false);

  errorMessage = signal<string | null>(null);

  submitted = signal(false);

  submitting = signal(false);

  activationEmail = signal('');

  currentStep = signal<'form' | 'confirmation' | 'federated'>('form');

  idpName = signal<string | null>(null);

  messages = signal({
    emailInput: '',
  });

  private readonly allowedCorporateDomains = [
    'comcast.com',
    'cable.comcast.com',
    'xfinity.com',
    'xfinitymail.com',
    'nbcuni.com',
    'nbc.com',
    'nbcuniversal.com',
    'spa.com',
  ];

  private isDomainOrSubdomain(domain: string, base: string): boolean {
    return domain === base || domain.endsWith(`.${base}`);
  }

  private nonPublicEmailValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const email = String(control.value ?? '')
      .trim()
      .toLowerCase();
    const at = email.lastIndexOf('@');
    if (at < 0 || at === email.length - 1) return null;

    const domain = email.slice(at + 1);

    if (
      this.allowedCorporateDomains.some((d) =>
        this.isDomainOrSubdomain(domain, d),
      )
    ) {
      return null;
    }

    return isFreeEmail(email) ? { publicEmailDomain: true } : null;
  };

  private getEmailValidators(user: User | null) {
    const validators = [
      Validators.required,
      Validators.email,
      Validators.maxLength(254),
    ];

    if (!this.hasNitelInternalPermission(user)) {
      validators.push(this.nonPublicEmailValidator);
    }

    return validators;
  }

  private hasNitelInternalPermission(user: User | null): boolean {
    return user?.Permissions?.includes(Permission.NITEL_INTERNAL) ?? false;
  }

  private updateEmailValidators(user: User | null): void {
    const emailControl = this.formGroup.controls.email;
    emailControl.setValidators(this.getEmailValidators(user));
    emailControl.updateValueAndValidity({ emitEvent: false });
  }

  formGroup: FormGroup<{
    email: FormControl<string>;
  }> = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,

      validators: this.getEmailValidators(null),
    }),
  });

  ngOnInit(): void {
    this.meta.updateTag({ httpEquiv: 'Cache-Control', content: 'no-cache, no-store, must-revalidate' });
    this.meta.updateTag({ httpEquiv: 'Pragma', content: 'no-cache' });
    this.meta.updateTag({ httpEquiv: 'Expires', content: '0' });

    // Restore state after page refresh
    const savedStep = sessionStorage.getItem(OKTA_MIGRATION_STORAGE_KEY_STEP);
    const savedEmail = sessionStorage.getItem(OKTA_MIGRATION_STORAGE_KEY_EMAIL);
    const savedExpiresAt = Number(
      sessionStorage.getItem(OKTA_MIGRATION_STORAGE_KEY_EXPIRES_AT),
    );
    const hasValidSavedState =
      savedStep &&
      savedEmail &&
      Number.isFinite(savedExpiresAt) &&
      savedExpiresAt > Date.now();

    if (!hasValidSavedState && (savedStep || savedEmail || savedExpiresAt)) {
      this.clearPersistedState();
    } else if (savedStep === 'confirmation' && savedEmail) {
      this.currentStep.set('confirmation');
      this.activationEmail.set(savedEmail);
      this.migrationTimer.start();
      this.setupPopStateGuard();
    } else if (savedStep === 'federated' && savedEmail) {
      this.currentStep.set('federated');
      this.activationEmail.set(savedEmail);
      this.migrationTimer.start(FEDERATED_LOGOUT_SECONDS);
      this.setupPopStateGuard();
    }

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        map((user) => {
          this.loggedInUser.set(user);
          this.updateEmailValidators(user);
          if (user?.Email) {
            this.formGroup.controls.email.setValue(user.Email);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.focusElement('#transitionEmailId');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.popStateListener !== null) {
      window.removeEventListener('popstate', this.popStateListener);
    }
    this.clearPersistedState();
  }

  submit(): void {
    this.submitted.set(true);
    this.messages.update((m) => ({ ...m, emailInput: '' }));
    this.errorMessage.set(null);

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();

      const emailControl = this.formGroup.controls.email;
      const errors = emailControl.errors as Record<string, boolean> | null;

      if (errors?.required) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Please enter your email address.',
        }));
      } else if (errors?.email) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Please enter a valid email address.',
        }));
      } else if (errors?.maxlength) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Email address must be 254 characters or fewer.',
        }));
      } else if (errors?.publicEmailDomain) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Please use a company email address (no public domains).',
        }));
      }
      return;
    }

    const email = this.formGroup.controls.email.value.trim();

    this.isLoading.set(true);
    this.oktaUserService
      .startOktaMigration(email)
      .pipe(
        take(1),
        catchError(
          (
            error:
              | HttpErrorResponse
              | { code: string; message: string; status: string },
          ) => {
            this.isLoading.set(false);
            this.errorMessage.set(
              'code' in error && error.code === 'DUPLICATE_VALUE'
                ? 'An account with this email already exists. Please use a different email address.'
                : "We couldn't start your transition right now. Please try again later.",
            );
            return throwError(() => error);
          },
        ),
      )
      .subscribe((res) => {
        this.isLoading.set(false);
        this.activationEmail.set(email);

        const isFederated = this.isFederatedMigrationResponse(res);

        if (isFederated) {
          this.currentStep.set('federated');
          this.persistMigrationState(
            'federated',
            email,
            FEDERATED_LOGOUT_SECONDS,
          );
          this.migrationTimer.start(FEDERATED_LOGOUT_SECONDS);
        } else {
          this.currentStep.set('confirmation');
          this.persistMigrationState('confirmation', email);
          this.migrationTimer.start();
        }
        this.focusElement('#step-heading');
        this.setupPopStateGuard();
      });
  }

  resendEmail(): void {
    if (!this.activationEmail()) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.oktaUserService
      .resendActivationEmail(this.activationEmail())
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.errorMessage.set(
            "We couldn't resend the activation email right now. Please try again later.",
          );
          return throwError(() => error);
        }),
      )
      .subscribe(() => {
        this.isLoading.set(false);
        this.snackBar.openFromComponent(ToastNotificationComponent, {
          duration: 7000,
          panelClass: ['toast-panel'],
          data: {
            message: 'Activation email resent successfully.',
            type: 'success',
          },
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      });
  }

  signOut(): void {
    this.clearPersistedState();
    this.migrationTimer.signOutNow();
  }

  goToDashboard(): void {
    this.clearPersistedState();
    this.router.navigate(['/dashboard']);
  }

  private persistMigrationState(
    step: 'confirmation' | 'federated',
    email: string,
    seconds = 180,
  ): void {
    sessionStorage.setItem(OKTA_MIGRATION_STORAGE_KEY_STEP, step);
    sessionStorage.setItem(OKTA_MIGRATION_STORAGE_KEY_EMAIL, email);
    sessionStorage.setItem(
      OKTA_MIGRATION_STORAGE_KEY_EXPIRES_AT,
      String(Date.now() + seconds * 1000),
    );
  }

  private focusElement(selector: string): void {
    setTimeout(() => {
      try {
        this.renderer.selectRootElement(selector).focus();
      } catch {
        // no-op
      }
    });
  }

  private isFederatedMigrationResponse(res: unknown): boolean {
    if (!res || typeof res !== 'object') return false;

    const response = res as Record<string, unknown>;
    const data =
      response.data && typeof response.data === 'object'
        ? (response.data as Record<string, unknown>)
        : {};
    const user =
      data.user && typeof data.user === 'object'
        ? (data.user as Record<string, unknown>)
        : {};

    const authProvider = [
      response.authProvider,
      data.authProvider,
      user.authProvider,
    ].find((value) => typeof value === 'string');

    if (
      String(authProvider ?? '')
        .toUpperCase()
        .includes('FEDERATION')
    ) {
      return true;
    }

    return [
      response.federated,
      response.userFederated,
      response.domainFederated,
      data.federated,
      data.userFederated,
      data.domainFederated,
      user.federated,
      user.userFederated,
      user.domainFederated,
    ].some((value) => value === true);
  }

  private setupPopStateGuard(): void {
    window.history.pushState({ step: 'confirmation' }, '');
    this.popStateListener = () => {
      if (this.currentStep() === 'confirmation') {
        window.history.pushState({ step: 'confirmation' }, '');
      }
    };
    window.addEventListener('popstate', this.popStateListener);
  }

  private clearPersistedState(): void {
    sessionStorage.removeItem(OKTA_MIGRATION_STORAGE_KEY_STEP);
    sessionStorage.removeItem(OKTA_MIGRATION_STORAGE_KEY_EMAIL);
    sessionStorage.removeItem(OKTA_MIGRATION_STORAGE_KEY_EXPIRES_AT);
  }
}
